import React, { useState, useEffect, useCallback, useRef } from "react";
import ReverseGeocodeComponent from "./ReverseGeocodeComponent";
import GeoapifyComponent from "./GeoapifyComponent";

const LocationModal = ({
    showLocationModal,
    setShowLocationModal,
    setCurrentLocation,
    setIsLocationSet,
    locationLoading,
    setLocationLoading,
    defaultFetchLocation,
    currentLocation,
    isLocationSet,
    onRemoveLocation,
}) => {
    const [fetchedLocation, setFetchedLocation] = useState(null);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [geoError, setGeoError] = useState(null);

    // Manual search state
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const debounceTimer = useRef(null);

    const API_KEY = "b20adba3a72241948593ece1f0e8f68e";

    // Close modal
    const handleCloseModal = () => {
        if (!isLocationSet && !localStorage.getItem("currentLocation")) {
            setGeoError("Please detect your location or search your city/pincode below to view available products.");
            return;
        }
        setShowLocationModal(false);
        setFetchedLocation(null);
        setGeoError(null);
        setSearchQuery("");
        setSearchResults([]);
    };

    const handleForceClose = () => {
        setShowLocationModal(false);
        setFetchedLocation(null);
        setGeoError(null);
        setSearchQuery("");
        setSearchResults([]);
    };

    // Auto-fetch location when modal opens if coordinates not available
    useEffect(() => {
        const storedLat = localStorage.getItem("latitude");
        const storedLng = localStorage.getItem("longitude");
        const storedLocation = localStorage.getItem("currentLocation");

        if (showLocationModal && (!storedLat || !storedLng || !storedLocation)) {
            handleFetchLocation();
        }
    }, [showLocationModal]);

    // Handle Manual Location Search with Geoapify + OpenStreetMap Nominatim for 100% POI/Landmark coverage (e.g. Coding Army)
    const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setGeoError(null);

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (!value.trim() || value.trim().length < 2) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        debounceTimer.current = setTimeout(async () => {
            try {
                const queryText = value.trim();

                const [geoapifyRes, nominatimRes] = await Promise.allSettled([
                    fetch(`https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(queryText)}&countrycode=in&apiKey=${API_KEY}`),
                    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryText)}&countrycodes=in&limit=8`)
                ]);

                let combinedResults = [];

                if (geoapifyRes.status === 'fulfilled') {
                    const json = await geoapifyRes.value.json();
                    if (json.features) {
                        const geoResults = json.features.map(f => ({
                            name: f.properties.formatted,
                            lat: f.properties.lat,
                            lon: f.properties.lon,
                            source: 'Geoapify'
                        }));
                        combinedResults.push(...geoResults);
                    }
                }

                if (nominatimRes.status === 'fulfilled') {
                    const json = await nominatimRes.value.json();
                    if (Array.isArray(json)) {
                        const nomResults = json.map(item => ({
                            name: item.display_name,
                            lat: parseFloat(item.lat),
                            lon: parseFloat(item.lon),
                            source: 'OSM'
                        }));
                        combinedResults.push(...nomResults);
                    }
                }

                // Deduplicate search results
                const uniqueResults = [];
                const seen = new Set();
                for (const item of combinedResults) {
                    const key = item.name.toLowerCase().slice(0, 30);
                    if (!seen.has(key)) {
                        seen.add(key);
                        uniqueResults.push(item);
                    }
                }

                setSearchResults(uniqueResults.slice(0, 10));
            } catch (err) {
                console.error("Location search error:", err);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 350);
    };

    const handleSelectSearchResult = (result) => {
        const formattedAddress = result.name || result.properties?.formatted;
        const lat = result.lat || result.properties?.lat;
        const lon = result.lon || result.properties?.lon;

        setCurrentLocation(formattedAddress);
        localStorage.setItem("latitude", lat);
        localStorage.setItem("longitude", lon);
        localStorage.setItem("currentLocation", formattedAddress);
        setIsLocationSet(true);
        window.dispatchEvent(new CustomEvent("locationFetched"));

        handleForceClose();
    };

    // Fetch current GPS location with 2-phase fallback (High accuracy -> Low accuracy)
    const handleFetchLocation = () => {
        setGeoError(null);
        if (!navigator.geolocation) {
            setGeoError("Your browser does not support automatic location detection. Please search your location manually below.");
            return;
        }

        setLocationLoading(true);

        // Phase 1: High Accuracy GPS
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setLatitude(latitude);
                setLongitude(longitude);
                setLocationLoading(false);
                setGeoError(null);
            },
            (error) => {
                console.warn("High accuracy geo error, attempting low accuracy fallback...", error);

                if (error.code === error.PERMISSION_DENIED) {
                    setGeoError("permission_denied");
                    setLocationLoading(false);
                    return;
                }

                // Phase 2: Low Accuracy Fallback for timeouts/weak GPS signal
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        setLatitude(latitude);
                        setLongitude(longitude);
                        setLocationLoading(false);
                        setGeoError(null);
                    },
                    (err2) => {
                        console.error("Fallback geo error:", err2);
                        if (err2.code === err2.PERMISSION_DENIED) {
                            setGeoError("permission_denied");
                        } else {
                            setGeoError("Unable to auto-detect GPS location. Please search your City, Area or Pincode manually below.");
                        }
                        setLocationLoading(false);
                    },
                    { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
                );
            },
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 0 }
        );
    };

    const handleAddressFetched = useCallback((locationData) => {
        if (locationData) {
            setFetchedLocation({
                address: locationData.address,
                latitude: locationData.latitude,
                longitude: locationData.longitude,
            });
        } else {
            setFetchedLocation({
                address: "No address found for these coordinates.",
                latitude: latitude,
                longitude: longitude,
            });
        }
    }, [latitude, longitude]);

    const handleSelectFetchedLocation = () => {
        if (fetchedLocation) {
            setCurrentLocation(fetchedLocation.address);
            localStorage.setItem("latitude", fetchedLocation.latitude);
            localStorage.setItem("longitude", fetchedLocation.longitude);
            localStorage.setItem("currentLocation", fetchedLocation.address);
            setIsLocationSet(true);
            window.dispatchEvent(new CustomEvent("locationFetched"));
            handleForceClose();
        }
    };

    if (!showLocationModal) return null;

    return (
        <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: 1055 }}
            tabIndex={-1}
        >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
                <div
                    className="modal-content border-0 shadow-lg"
                    style={{ borderRadius: "16px", overflow: "hidden" }}
                >
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center p-4 border-bottom bg-white">
                        <div className="d-flex align-items-center gap-2">
                            <i className="fas fa-map-marker-alt text-warning fs-5" style={{ color: "#a37c4d" }} />
                            <h5 className="mb-0 fw-bold text-dark">Select Delivery Location</h5>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleForceClose}
                            style={{ fontSize: "14px" }}
                        />
                    </div>

                    <div className="p-4" style={{ maxHeight: "80vh", overflowY: "auto" }}>
                        {/* Active Location Banner */}
                        {isLocationSet && currentLocation && (
                            <div className="border rounded p-3 mb-3 bg-light">
                                <div className="d-flex align-items-start gap-3">
                                    <i className="fas fa-check-circle text-success mt-1 fs-5" />
                                    <div className="flex-grow-1">
                                        <h6 className="mb-1 fw-semibold text-dark">Active Location</h6>
                                        <p className="mb-2 text-muted small">{currentLocation}</p>
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={onRemoveLocation}
                                            style={{ borderRadius: "6px" }}
                                        >
                                            Change / Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Inline Error Banners */}
                        {geoError === "permission_denied" && (
                            <div className="alert alert-warning border-warning p-3 mb-3" style={{ borderRadius: "12px", backgroundColor: "#fffdf0" }}>
                                <div className="d-flex align-items-start gap-2 mb-2">
                                    <i className="fas fa-lock text-warning fs-5 mt-1" />
                                    <div>
                                        <h6 className="fw-bold mb-1 text-dark">Location Access Blocked in Chrome</h6>
                                        <p className="mb-2 small text-secondary">
                                            Your browser blocked location access. You can easily unblock it right here without leaving:
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white p-3 rounded border small mb-3 text-dark">
                                    <p className="fw-bold mb-1 text-dark">📌 How to Allow Location in Chrome / Edge:</p>
                                    <ol className="mb-0 ps-3 text-secondary" style={{ fontSize: "13px" }}>
                                        <li className="mb-1">Click the <b>🔒 Padlock / Tune icon</b> at the top-left of your address bar (near website URL).</li>
                                        <li className="mb-1">Toggle <b>Location permission</b> to <span className="badge bg-success">Allow</span>.</li>
                                        <li>Click <b>"Reload & Retry"</b> below, OR search any place/city/landmark in the search box!</li>
                                    </ol>
                                </div>

                                <button
                                    className="btn btn-sm text-white fw-bold px-3 py-2 w-100"
                                    style={{ backgroundColor: "#a37c4d", borderRadius: "8px" }}
                                    onClick={() => window.location.reload()}
                                >
                                    🔄 Reload Page & Retry Location Detection
                                </button>
                            </div>
                        )}

                        {geoError && geoError !== "permission_denied" && (
                            <div className="alert alert-info border-info d-flex align-items-start gap-2 p-3 mb-3" style={{ borderRadius: "10px" }}>
                                <i className="fas fa-info-circle text-info fs-5 mt-1" />
                                <p className="mb-0 small">{geoError}</p>
                            </div>
                        )}

                        {/* GPS Auto-Detect Button */}
                        <div className="mb-4">
                            <button
                                className="btn w-100 d-flex align-items-center justify-content-center gap-3 py-3"
                                style={{
                                    border: "2px solid #a37c4d",
                                    borderRadius: "12px",
                                    backgroundColor: locationLoading ? "#f8f9fa" : "#fff8f0",
                                    color: "#a37c4d",
                                    fontWeight: "600",
                                    transition: "all 0.2s"
                                }}
                                onClick={handleFetchLocation}
                                disabled={locationLoading}
                            >
                                <i className="fas fa-crosshairs fs-5" />
                                <span>
                                    {locationLoading ? "Detecting GPS location..." : "📍 Detect Current Location"}
                                </span>
                                {locationLoading && (
                                    <div className="spinner-border spinner-border-sm text-warning ms-2" role="status" />
                                )}
                            </button>
                        </div>

                        {/* Divider */}
                        <div className="d-flex align-items-center my-3">
                            <hr className="flex-grow-1 my-0" />
                            <span className="px-3 text-muted small fw-semibold">OR SEARCH ANY LANDMARK / AREA / CITY</span>
                            <hr className="flex-grow-1 my-0" />
                        </div>

                        {/* Search Input Box */}
                        <div className="mb-3 position-relative">
                            <div className="input-group">
                                <span className="input-group-text bg-white border-end-0" style={{ borderColor: "#ced4da", borderRadius: "10px 0 0 10px" }}>
                                    <i className="fas fa-search text-muted" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 py-2"
                                    placeholder="Search Landmark, Society, Shop, City or Pincode (e.g. Coding Army, Indore)"
                                    value={searchQuery}
                                    onChange={handleSearchInput}
                                    style={{ borderRadius: "0 10px 10px 0", fontSize: "14px" }}
                                />
                                {isSearching && (
                                    <span className="position-absolute end-0 top-50 translate-middle-y me-3" style={{ zIndex: 5 }}>
                                        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                    </span>
                                )}
                            </div>

                            {/* Search Results Dropdown */}
                            {searchResults.length > 0 && (
                                <div
                                    className="list-group shadow mt-1 border"
                                    style={{
                                        maxHeight: "220px",
                                        overflowY: "auto",
                                        borderRadius: "10px",
                                        zIndex: 1000
                                    }}
                                >
                                    {searchResults.map((item, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className="list-group-item list-group-item-action d-flex align-items-start gap-2 p-2"
                                            onClick={() => handleSelectSearchResult(item)}
                                        >
                                            <i className="fas fa-map-marker-alt text-danger mt-1 small" />
                                            <div className="text-start">
                                                <div className="fw-semibold small text-dark">
                                                    {item.name || item.properties?.formatted}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Reverse Geocode component for GPS detect */}
                        {latitude && longitude && (
                            <GeoapifyComponent
                                latitude={latitude}
                                longitude={longitude}
                                onAddressFetched={handleAddressFetched}
                            />
                        )}

                        {/* Detected GPS Address Confirm Card */}
                        {fetchedLocation && (
                            <div className="border rounded p-3 mt-3 bg-light">
                                <div className="d-flex align-items-start gap-3">
                                    <i className="fas fa-map-marker-alt text-primary mt-1" />
                                    <div className="flex-grow-1">
                                        <h6 className="mb-1 fw-semibold">Detected GPS Address</h6>
                                        <p className="mb-2 text-muted small">{fetchedLocation.address}</p>
                                        <button
                                            className="btn btn-primary btn-sm px-3"
                                            onClick={handleSelectFetchedLocation}
                                            style={{
                                                borderRadius: "6px",
                                                backgroundColor: "#a37c4d",
                                                borderColor: "#a37c4d",
                                            }}
                                        >
                                            Confirm Location
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationModal;
